import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import CartItem from './CartItem';
import { formatCurrency } from '../../../utils';
import { Heading1 } from '../../typography';
import { SOFT_TOMATO } from '../../colors';
import { useCart } from '../../../contexts/CartContext';
import mixpanel from '../../../utils/mixpanel';
import { MIXPANEL_PAGE } from '../../../utils/constants';

const LogoImg = require('../../../assets/hg-logo.png');

interface CartModalProps {
  onProceedCheckout?: () => void;
}

const Cart = ({
  onProceedCheckout
}: CartModalProps) => {
  
  const { 
    cart:products, 
    addToCart,
    onDecreaseQuantity,
    removeFromCart, 
    isCartOpen, 
    setIsCartOpen
  } = useCart();

  const totalAmount = useMemo(() => {
    let amount = 0;
    products.forEach((product) => product.units.forEach((unit) => {
      if (Number(unit.quantity) > 0) {
        amount += (Number(unit.quantity) * Number(unit.price));
      }
    }))
    return amount;
  }, [products])

  useEffect(() => {
    if (isCartOpen) {
      mixpanel.track(`${MIXPANEL_PAGE.CART} - Page View`);
    }
  }, [isCartOpen])

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          {/* Overlay */}
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
          />

          {/* Sidebar Modal */}
          <Modal
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween' }}
          >
            <Content>
              <Heading>
                <Logo>
                  <div>
                    <img src={LogoImg} />
                  </div>
                </Logo>
                
                <h1>
                  CART
                </h1>

                <ButtonClose
                  onClick={() => setIsCartOpen(false)}
                >
                  X
                </ButtonClose>
              </Heading>
              <>
                { products.map((product) => (
                  <CartItem 
                    thumbnail={product.images[0]}
                    name={product.name}
                    amount={formatCurrency(product.units[0].price)}
                    units={product.units || []}
                    onRemove={() => {
                      removeFromCart(product._id)
                    }}
                    onIncrease={(unitType) =>  addToCart(product, unitType)}
                    onDecrease={(unitType) =>  onDecreaseQuantity(product, unitType)}
                  />
                ))}
              </>
            </Content>

            <Checkout>
              <CheckoutAmount>
                <p>ESTIMATED PRICE:</p>
                <p>{formatCurrency(totalAmount)}</p>
              </CheckoutAmount>
              <button
                onClick={() => onProceedCheckout?.()}
              >
                CHECKOUT
              </button>
            </Checkout>
            
          </Modal>
        </>
      )}
    </AnimatePresence>
  )
}

export default Cart;

const Overlay = styled(motion.div)`
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  background: black;
  opacity: 0.5;
  z-index: 99;
`;

const Modal = styled(motion.div)`
  box-sizing: border-box;
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  background: #fff;
  z-index: 100;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  border-radius: 0;
  width: 100vw;

  @media (min-width: 768px) {
    width: 400px;
    border-left: 1px solid #ddd;
  }
`;

const Content = styled.div`
  padding: 0rem 1rem;
`;

const Heading = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${SOFT_TOMATO};

  h1 {
    text-align: left;
    padding-left: 0.5rem;
    flex: 1;
    font-family: 'Poppins-Light';
    font-size: 1.0rem;
  }
`;

const Logo = styled.div`
  height: 24px;

   img {
    height: 24px;
    object-fit: cover;
  }
`;

const ButtonClose = styled.button`
    height: 24px;
    width: 24px;
    background-color: ${SOFT_TOMATO};
    color: white;
    border: none;
    border-radius: 12px;
    
    p {
        font-family: 'Poppins-SemiBold';
        font-size: 0.8rem;
    }
`;

const Checkout = styled.div`
  position: sticky;
  z-index: 1;
  bottom: 0;
  widht: auto;
  padding: 1rem;
  background: #fff;


  button {
    width: 100%;
    font-family: 'Poppins-Bold';
    font-size: 0.8rem;
    background-color: ${SOFT_TOMATO};
    color: #fff;
    border: none;
    padding: 0.5rem 0rem;
    border-radius: 12px;
  }
`;

const CheckoutAmount = styled.div`
  display: flex;
  justify-content: space-between;
  
  p {
    font-family: 'Poppins-SemiBold';
    font-size: 1rem;
  }
`;